import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconTypes } from '../components/foundations/Icon';
import css from './CreativeIQLandingPage.css';
import Button from '../components/atoms/Button';
import LandingPageIcon from '../resources/icons/creatives/creative_iq_landing_page.svg';
import API from '../services/api';
import { getUserData } from '../selectors/user';
import toaster from '../utils/toaster';
import FeaturesSection from '../components/organisms/FeaturesSection';

const FEATURES = [
    {
        iconName: 'chart-treemap',
        translationKey: 'CREATIVE_GALLERY',
        iconBackgroundColor: 'var(--purple100)',
    },
    {
        iconName: 'tags',
        translationKey: 'AI_TAGGING',
        iconBackgroundColor: 'var(--teal100)',
    },
    {
        iconName: 'table-row',
        translationKey: 'EXPLORE_REPORT',
        iconBackgroundColor: 'var(--blue150)',
    },
    {
        iconName: 'settings-spark',
        translationKey: 'CUSTOM_DIMENSIONS',
        iconBackgroundColor: 'var(--green100)',
    },
    {
        iconName: 'users-group',
        translationKey: 'CROSS_TEAM',
        iconBackgroundColor: 'var(--orange100)',
    },
    {
        iconName: 'external-link',
        translationKey: 'LEARN_MORE',
        iconBackgroundColor: 'var(--gray200)',
    },
];

const api = new API();

const URL_CREATIVE_IQ_START_TRIAL = '/api/start_creative_iq_trial';

const hasTrialEnded = trialEndDate => trialEndDate && moment(trialEndDate).diff(new Date(), 'days') < 0;

function CreativeIQLandingPage({ translate }) {
    const navigate = useNavigate();

    const { trials_config: trialsConfig, is_org_admin: isOrgAdmin } = useSelector(state => getUserData(state));

    const [hasStartedTrial, setHasStartedTrial] = React.useState(false);

    const startTrial = () => {
        setHasStartedTrial(true);
        api.getJson(URL_CREATIVE_IQ_START_TRIAL, {
            method: 'POST',
            credentials: 'include',
            cache: false,
            unique: true,
        })
            .then(() => {
                navigate(0);
            })
            .catch(() => {
                toaster.error(translate('STATIC.CREATIVE_IQ.START_TRIAL_ERROR'));
            });
    };

    const trialEndDate = trialsConfig?.creative_iq?.end_date;

    return (
        <div className={css.container}>
            <div className={css.headerSection}>
                <div className={css.leftContent}>
                    <div className={css.title}>
                        <div className={css.featureName}>
                            <Icon name="ai-line" type={IconTypes.MINGCUTE} interactive={false} />
                            <Translate id="STATIC.CREATIVE_IQ.FEATURE_NAME" />
                        </div>
                        <Translate id="STATIC.CREATIVE_IQ.HEADING" />
                    </div>
                    <div className={css.description}>
                        <Translate id="STATIC.CREATIVE_IQ.DESCRIPTION" />
                        {!hasTrialEnded(trialEndDate) && (
                            <div className={css.trialContainer}>
                                <Button
                                    disabled={hasStartedTrial || !isOrgAdmin}
                                    showSpinner={hasStartedTrial}
                                    className={css.trialButton}
                                    onClick={startTrial}
                                >
                                    <Translate
                                        id={`STATIC.CREATIVE_IQ.${
                                            trialEndDate ? 'RESUME_TRIAL_BUTTON' : 'TRY_FREE_BUTTON'
                                        }`}
                                    />
                                </Button>
                                {!isOrgAdmin && <Translate id="STATIC.CREATIVE_IQ.UNAUTHORIZED_TO_START_TRIAL" />}
                            </div>
                        )}
                    </div>
                </div>
                <LandingPageIcon className={css.landingPageIcon} />
            </div>
            <FeaturesSection features={FEATURES} pageName="CREATIVE_IQ" numberColumns={3} />
        </div>
    );
}

CreativeIQLandingPage.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(CreativeIQLandingPage);
